/**
 * DEV-7091b — adaptive typewriter for streamed assistant text.
 *
 * Faithful port of the Vue 2 `createTypewriter`: buffers incoming tokens and
 * reveals them gradually via requestAnimationFrame, releasing more characters
 * per frame as the backlog grows. `append` is supplied by the caller (it appends
 * a chunk to the reactive message content).
 */
export interface Typewriter {
  push(text: string): void
  finish(onComplete?: () => void): void
  flush(): void
  stop(): void
}

function schedule(cb: () => void): number {
  if (typeof requestAnimationFrame === 'function') return requestAnimationFrame(() => cb())
  return setTimeout(cb, 16) as unknown as number
}

function unschedule(id: number): void {
  if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(id)
  else clearTimeout(id)
}

export function createTypewriter(append: (chunk: string) => void): Typewriter {
  let queue = ''
  let timer: number | null = null
  let streamDone = false
  let onComplete: (() => void) | null = null

  function drain(): void {
    if (!queue.length) {
      timer = null
      if (streamDone && onComplete) {
        onComplete()
        onComplete = null
      }
      return
    }
    // Adaptive speed: faster when the queue is large, slower when small.
    const len = queue.length
    const chars = len > 500 ? 6 : len > 200 ? 4 : len > 50 ? 2 : 1
    const chunk = queue.substring(0, chars)
    queue = queue.substring(chars)
    append(chunk)
    timer = schedule(drain)
  }

  return {
    push(text: string): void {
      queue += text
      if (timer === null) timer = schedule(drain)
    },
    finish(cb?: () => void): void {
      streamDone = true
      onComplete = cb ?? null
      if (!queue.length && timer === null && onComplete) {
        onComplete()
        onComplete = null
      }
    },
    flush(): void {
      if (timer !== null) {
        unschedule(timer)
        timer = null
      }
      if (queue.length) {
        append(queue)
        queue = ''
      }
      streamDone = true
    },
    stop(): void {
      if (timer !== null) {
        unschedule(timer)
        timer = null
      }
      streamDone = true
    },
  }
}
